import { Base } from '@studiometa/js-toolkit';
import { toggleClass } from '@studiometa/js-toolkit/utils';

/**
 * @typedef {object} PushSliderRefs
 * @property {HTMLElement[]} pushs[]
 * @property {HTMLElement[]} images[]
 * @property {HTMLElement} current
 * @property {HTMLElement} progress
 */

/**
 * @typedef {object} PushSliderOptions
 * @property {string} activePushClassNames
 * @property {string} inactivePushClassNames
 * @property {string} activeImageClassNames
 * @property {string} inactiveImageClassNames
 */

/**
 * @typedef {object} PushSliderInterface
 * @property {PushSliderRefs} $refs
 * @property {PushSliderOptions} $options
 */

/**
 * PushSlider class.
 */
export default class PushSlider extends Base {
  static config = {
    name: 'PushSlider',
    refs: ['pushs[]', 'images[]', 'current', 'progress'],
    options: {
      interval: {
        type: Number,
        default: 3000,
      },
      activePushClassNames: {
        type: String,
        default: '',
      },
      inactivePushClassNames: {
        type: String,
        default: '',
      },
      activeImageClassNames: {
        type: String,
        default: '',
      },
      inactiveImageClassNames: {
        type: String,
        default: '',
      },
    },
  };

  /**
   * Slide index
   * @type {number}
   */
  index = 0;

  /**
   * Slides
   * @type {Array.<{image: HTMLElement, push: HTMLElement}>}
   */
  slides = [];

  /**
   * Slides count
   * @type {number}
   */
  count = 0;

  /**
   * PushSlider mounted
   * @this {PushSlider & PushSliderInterface}
   */
  mounted() {
    this.count = this.$refs.pushs.length;

    this.slides = [...Array(this.count)].map((_, index) => ({
      image: this.$refs.images[index],
      push: this.$refs.pushs[index],
    }));

    this.setIndicators();

    setInterval(() => {
      this.index = (this.index + 1) % this.count;
      this.animate();
      this.setIndicators();
    }, this.$options.interval);
  }

  /**
   * Set indicators
   */
  setIndicators() {
    this.$refs.current.innerHTML = this.index + 1;
    this.$refs.progress.style.transform = `translateX(${
      (100 * (this.index + 1)) / this.count - 100
    }%)`;
  }

  /**
   * Animate
   * @this {PushSlider & PushSliderInterface}
   */
  animate() {
    this.slides.forEach((slide, index) => {
      const { image, push } = slide;
      const isActive = index === this.index;

      toggleClass(image, this.$options.activeImageClassNames, isActive);
      toggleClass(image, this.$options.inactiveImageClassNames, !isActive);

      toggleClass(push, this.$options.activePushClassNames, isActive);
      toggleClass(push, this.$options.inactivePushClassNames, !isActive);
    });
  }
}
